import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';

export interface TokenUsageParams {
  userId: string;
  sessionId: string;
  requestType: 'completion' | 'embedding';
  model: string;
  tokensUsed: number;
  estimatedCost?: number;
}

export interface PerformanceLogParams {
  requestId: string;
  userId: string;
  operationType: string;
  requestStartTime: Date | number;  // Allow both Date objects and timestamps
  processingDuration: number;
  totalDuration: number;
  isCached: boolean;
  topic?: string;
  status: 'success' | 'error' | 'timeout';
  errorMessage?: string;
}

export interface KnowledgeEffectivenessParams {
  knowledgeDocumentId: string;
  requestId: string;
  sessionId: string;
  userId: string;
  relevanceScore: number;
  wasUsed: boolean;
  feedbackScore?: number;
}

// Constants for integer limits
const MAX_SAFE_INTEGER = 2147483647; // PostgreSQL integer max value (2^31 - 1)
const MIN_SAFE_INTEGER = 0;          // We won't allow negative durations

// Track token usage
export const trackTokenUsage = async (params: TokenUsageParams) => {
  try {
    const { userId, sessionId, requestType, model, tokensUsed, estimatedCost } = params;
    
    // Calculate estimated cost if not provided
    let calculatedCost = estimatedCost;
    if (calculatedCost === undefined) {
      // Default pricing (can be adjusted based on actual model prices)
      const pricePerToken = requestType === 'completion' 
        ? 0.000002  // $0.002 per 1000 tokens for completion
        : 0.0000004; // $0.0004 per 1000 tokens for embedding
      calculatedCost = tokensUsed * pricePerToken;
    }
    
    const { error } = await supabase
      .from('ai_token_usage')
      .insert({
        user_id: userId,
        session_id: sessionId,
        request_type: requestType,
        model: model,
        tokens_used: tokensUsed,
        estimated_cost: calculatedCost
      });
    
    if (error) {
      console.error('Error tracking token usage:', error);
    }
    
    return { success: !error };
  } catch (err) {
    console.error('Error in trackTokenUsage:', err);
    return { success: false };
  }
};

// Start tracking performance - returns a request ID and timestamp to use for completion
export const startPerformanceTracking = () => {
  return {
    requestId: uuidv4(),
    requestStartTime: Date.now() // Return timestamp (number) instead of Date object
  };
};

// Complete performance tracking and log results
export const finishPerformanceTracking = async (
  params: PerformanceLogParams
) => {
  try {
    const { 
      requestId, 
      userId, 
      operationType, 
      requestStartTime, 
      processingDuration,
      totalDuration, 
      isCached, 
      topic, 
      status, 
      errorMessage 
    } = params;
    
    // Ensure durations are valid and within safe integer ranges for Postgres
    // If negative or NaN, default to 0. If too large, cap at MAX_SAFE_INTEGER
    const safeProcessingDuration = Math.max(
      MIN_SAFE_INTEGER,
      Math.min(
        isNaN(processingDuration) ? 0 : processingDuration,
        MAX_SAFE_INTEGER
      )
    );
    
    const safeTotalDuration = Math.max(
      MIN_SAFE_INTEGER,
      Math.min(
        isNaN(totalDuration) ? 0 : totalDuration,
        MAX_SAFE_INTEGER
      )
    );
    
    // Convert the start time to ISO string based on what type it is
    let safeStartTime: string;
    
    if (requestStartTime instanceof Date && !isNaN(requestStartTime.getTime())) {
      // If it's a valid Date object
      safeStartTime = requestStartTime.toISOString();
    } else if (typeof requestStartTime === 'number' && !isNaN(requestStartTime)) {
      // If it's a valid timestamp number
      safeStartTime = new Date(requestStartTime).toISOString();
    } else {
      // Fallback to current time if invalid
      console.warn('Invalid requestStartTime provided to finishPerformanceTracking', requestStartTime);
      safeStartTime = new Date().toISOString();
    }
    
    // Log debug info
    console.debug('Performance logging:', {
      requestId,
      operationType,
      originalProcessingDuration: processingDuration,
      safeProcessingDuration,
      originalTotalDuration: totalDuration,
      safeTotalDuration,
      originalStartTime: requestStartTime,
      safeStartTime
    });
    
    const { error } = await supabase
      .from('ai_performance_logs')
      .insert({
        request_id: requestId,
        user_id: userId,
        operation_type: operationType,
        request_start_time: safeStartTime,
        processing_duration: safeProcessingDuration,
        total_duration: safeTotalDuration,
        cached: isCached,
        topic: topic,
        status: status,
        error_message: errorMessage
      });
    
    if (error) {
      console.error('Error logging performance data:', error);
    }
    
    return { success: !error };
  } catch (err) {
    console.error('Error in finishPerformanceTracking:', err);
    return { success: false };
  }
};

// Track knowledge document effectiveness
export const trackKnowledgeEffectiveness = async (params: KnowledgeEffectivenessParams) => {
  try {
    const { 
      knowledgeDocumentId, 
      requestId, 
      sessionId, 
      userId, 
      relevanceScore, 
      wasUsed, 
      feedbackScore 
    } = params;
    
    const { error } = await supabase
      .from('knowledge_effectiveness')
      .insert({
        knowledge_document_id: knowledgeDocumentId,
        request_id: requestId,
        session_id: sessionId,
        user_id: userId,
        relevance_score: relevanceScore,
        was_used: wasUsed,
        feedback_score: feedbackScore
      });
    
    if (error) {
      console.error('Error tracking knowledge effectiveness:', error);
    }
    
    return { success: !error };
  } catch (err) {
    console.error('Error in trackKnowledgeEffectiveness:', err);
    return { success: false };
  }
};

// Get analytics dashboard data
export const getAnalyticsDashboardData = async (
  userId: string,
  startDate: Date,
  endDate: Date
) => {
  try {
    const [tokenUsageResponse, performanceResponse] = await Promise.all([
      supabase
        .from('ai_token_usage')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString()),
      
      supabase
        .from('ai_performance_logs')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
    ]);
    
    if (tokenUsageResponse.error) {
      console.error('Error fetching token usage:', tokenUsageResponse.error);
      return { success: false, error: tokenUsageResponse.error };
    }
    
    if (performanceResponse.error) {
      console.error('Error fetching performance logs:', performanceResponse.error);
      return { success: false, error: performanceResponse.error };
    }
    
    return { 
      success: true,
      data: {
        tokenUsage: tokenUsageResponse.data || [],
        performanceLogs: performanceResponse.data || []
      }
    };
  } catch (err) {
    console.error('Error in getAnalyticsDashboardData:', err);
    return { success: false, error: err };
  }
};
